import { useMutation } from '@tanstack/react-query';
import {
  observationService,
  ObservationUpsertProps,
} from '../services/observationService';
import { cacheInvalidationService } from '../services/cacheInvalidationService';
import { EMutationKey } from '../store/mutationKeys';
import { ObservableLevelEnumValue } from '../data/enums';

/**
 * Hook for creating/updating observations with automatic cache invalidation
 * Use this instead of calling observationService.upsertObservation directly
 */
function useUpsertObservation() {
  return useMutation({
    mutationKey: [EMutationKey.upsertObservation],
    mutationFn: async (params: ObservationUpsertProps) => {
      const result = await observationService.upsertObservation(params);
      return { result, inspectionId: params.inspectionId };
    },
    onSuccess: async ({ inspectionId }) => {
      // Invalidate caches related to this inspection
      await cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
      console.log(
        '✅ Cache invalidated after observation upsert for inspection:',
        inspectionId,
      );
    },
    onError: (error) => {
      console.error('❌ Failed to upsert observation:', error);
    },
    networkMode: 'always',
  });
}

/**
 * Hook for deleting observations by path with automatic cache invalidation
 */
function useDeleteObservationByPath() {
  return useMutation({
    mutationKey: [EMutationKey.deleteObservationByPath],
    mutationFn: async (params: {
      inspectionId: string;
      inspectionCategoryId: string;
      path: string;
    }) => {
      const result = await observationService.deleteObservationByPath(params);
      return { result, inspectionId: params.inspectionId };
    },
    onSuccess: async ({ inspectionId }) => {
      await cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
      console.log(
        '✅ Cache invalidated after observation deletion for inspection:',
        inspectionId,
      );
    },
    onError: (error) => {
      console.error('❌ Failed to delete observation by path:', error);
    },
    networkMode: 'always',
  });
}

/**
 * Hook for deleting observations by properties with automatic cache invalidation
 */
function useDeleteObservationsByProps() {
  return useMutation({
    mutationKey: [EMutationKey.deleteObservationsByProps],
    mutationFn: async (params: {
      inspectionId: string;
      inspectionCategoryId: string;
      entityId: number;
      entityExternalId: string;
      dataLevel: ObservableLevelEnumValue;
      path: string;
      unitId?: number;
    }) => {
      const result = await observationService.deleteObservationsByProps(params);
      return { result, inspectionId: params.inspectionId };
    },
    onSuccess: async ({ inspectionId }) => {
      await cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
      console.log(
        '✅ Cache invalidated after observations deletion for inspection:',
        inspectionId,
      );
    },
    onError: (error) => {
      console.error('❌ Failed to delete observations by props:', error);
    },
    networkMode: 'always',
  });
}

/**
 * Hook for deleting specific observation with automatic cache invalidation
 */
function useDeleteObservation() {
  return useMutation({
    mutationKey: [EMutationKey.deleteObservation],
    mutationFn: async (params: {
      inspectionId: string;
      inspectionCategoryTypeId: string;
      observationId: string;
    }) => {
      await observationService.deleteObservation(
        params.inspectionId,
        params.inspectionCategoryTypeId,
        params.observationId,
      );
      return { inspectionId: params.inspectionId };
    },
    onSuccess: async ({ inspectionId }) => {
      await cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
      console.log(
        '✅ Cache invalidated after specific observation deletion for inspection:',
        inspectionId,
      );
    },
    onError: (error) => {
      console.error('❌ Failed to delete observation:', error);
    },
    networkMode: 'always',
  });
}

/**
 * Combined hook for all observation mutations - provides all mutation methods in one hook
 * Use this when you need multiple observation operations in a single component
 */
export function useObservationMutations() {
  const upsertMutation = useUpsertObservation();
  const deleteByPathMutation = useDeleteObservationByPath();
  const deleteByPropsMutation = useDeleteObservationsByProps();
  const deleteMutation = useDeleteObservation();

  return {
    // Mutation functions
    upsertObservation: upsertMutation.mutate,
    upsertObservationAsync: upsertMutation.mutateAsync,
    deleteObservationByPath: deleteByPathMutation.mutate,
    deleteObservationByPathAsync: deleteByPathMutation.mutateAsync,
    deleteObservationsByProps: deleteByPropsMutation.mutate,
    deleteObservationsByPropsAsync: deleteByPropsMutation.mutateAsync,
    deleteObservation: deleteMutation.mutate,
    deleteObservationAsync: deleteMutation.mutateAsync,

    // Loading states
    isUpserting: upsertMutation.isPending,
    isDeletingByPath: deleteByPathMutation.isPending,
    isDeletingByProps: deleteByPropsMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isMutating:
      upsertMutation.isPending ||
      deleteByPathMutation.isPending ||
      deleteByPropsMutation.isPending ||
      deleteMutation.isPending,

    // Error states
    upsertError: upsertMutation.error,
    deleteByPathError: deleteByPathMutation.error,
    deleteByPropsError: deleteByPropsMutation.error,
    deleteError: deleteMutation.error,
    hasError:
      upsertMutation.isError ||
      deleteByPathMutation.isError ||
      deleteByPropsMutation.isError ||
      deleteMutation.isError,
  };
}
